import type { RequestFilters } from './filters.js';
import { chatUnreadSummaryKey, chatUnreadSummaryPrefixKey } from './request-unread/index.js';

/**
 * @aiPurpose React Query key factory for the requests feature (the read
 *   hooks live in `queries.read.ts`, the mutations in `mutations.ts`). Key
 *   shapes match `RealtimeBootstrap`'s invalidation calls exactly
 *   (`['requests']` and `['request', id]`); changing them here without
 *   updating `RealtimeBootstrap` silently breaks live invalidation.
 */
export const requestsKeys = {
  all: ['requests'] as const,
  list: (filters: Partial<RequestFilters>) => ['requests', filters] as const,
  // WHY: infinite-list (load-more) keeps a separate cache slot from the
  // single-page list because TanStack stores `InfiniteData<T>` rather than
  // a bare `T`. Sharing the same key would force every consumer to branch
  // on cache shape; a dedicated suffix removes that ambiguity.
  listInfinite: (filters: Partial<RequestFilters>) => ['requests', 'infinite', filters] as const,
  detail: (id: string) => ['request', id] as const,
  chat: (id: string) => ['request-chat', id] as const,
  files: (id: string) => ['request-files', id] as const,
  // WHY (#1753): the unread-summary key shape now lives in the narrow
  // `request-unread` capability (the chunked hook moved there so CRM My Day can
  // consume it without a crm → requests feature edge). These wire to the
  // capability's factories so `requestsKeys.chatUnreadSummary(ids)` /
  // `.chatUnreadSummaryPrefix()` stay byte-for-byte identical for every
  // existing consumer (mutations, realtime invalidation, tests).
  chatUnreadSummary: chatUnreadSummaryKey,
  chatUnreadSummaryPrefix: chatUnreadSummaryPrefixKey,
  carriersCatalog: () => ['carriers'] as const,
  // WHY (#2140): per-request return-snapshot list + per-snapshot detail.
  snapshots: (id: string) => ['request-snapshots', id] as const,
  snapshot: (id: string, snapshotId: string) => ['request-snapshot', id, snapshotId] as const,
};
